import type { ClientOrganizationProfile } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, type FC } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useGetCommentPushSetting } from '@/features/api/hooks/grapevine-organization/useGetCommentPushSetting';
import { useUpdateCommentPushSetting } from '@/features/api/hooks/grapevine-organization/useUpdateCommentPushSetting';
import {
  getOrganizationCommentPushSettingQueryKey,
  setOrganizationCommentPushEnabled
} from '@/features/profile-setting/utils/notificationSettingCache';
import { AppSwitch } from '@/shared/components/AppSwitch';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const DEFAULT_ENABLED = true;
const REQUEST_DEBOUNCE_MS = 150;

type Props = {
  organization: ClientOrganizationProfile;
};

export const OrganizationCommentPushSettingItem: FC<Props> = ({
  organization
}) => {
  const theme = useAppTheme();
  const queryClient = useQueryClient();
  const { id, name } = organization;

  const { data: setting } = useGetCommentPushSetting(id);
  const { mutate: updateSetting } = useUpdateCommentPushSetting();

  const isEnabled = setting?.commentPushEnabled ?? DEFAULT_ENABLED;

  const changeSetting = useDebouncedCallback(
    (enabled: boolean) =>
      updateSetting({ organizationId: id, commentPushEnabled: enabled }),
    REQUEST_DEBOUNCE_MS
  );

  const handleChange = useCallback(
    (enabled: boolean) => {
      void queryClient.cancelQueries({
        queryKey: getOrganizationCommentPushSettingQueryKey(id)
      });
      setOrganizationCommentPushEnabled(queryClient, id, enabled);

      changeSetting(enabled);
    },
    [changeSetting, id, queryClient]
  );

  return (
    <AppSwitch
      title={name}
      value={isEnabled}
      onValueChange={handleChange}
      backgroundColor={theme.colors.surfaceContainerLowest}
    />
  );
};
